import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';

const API = API_URL.replace(/\/$/, '');

export default function AdminStats() {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');
  const [overviewOpen, setOverviewOpen] = useState(true);
  const [newUsersOpen, setNewUsersOpen] = useState(true);
  const [recentOpen, setRecentOpen] = useState(true);
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  useEffect(() => {
    (async () => {
      try {
        setLoading(true); setErr(null);
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const params: any = {};
        if (from) params.from = from;
        if (to) params.to = to;
        const s = await axios.get(`${API}/admin/stats`, { headers, params });
        setStats(s.data || {});
      } catch (e: any) {
        setErr(e?.response?.data?.message || e?.message || 'Failed to load stats');
      } finally {
        setLoading(false);
      }
    })();
  }, [from, to]);

  return (
    <div className="p-6 space-y-6 themed-page">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-heading">Admin — Stats</h2>
        <div className="flex items-center gap-2">
          <input type="date" className="input px-2 py-1 text-xs rounded-md" value={from} onChange={(e) => setFrom(e.target.value)} />
          <input type="date" className="input px-2 py-1 text-xs rounded-md" value={to} onChange={(e) => setTo(e.target.value)} />
          {!loading && stats && (
            <div className="text-xs opacity-70">Updated {new Date().toLocaleTimeString()}</div>
          )}
        </div>
      </div>
      {err && <div className="text-red-500 text-sm">{err}</div>}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="rounded-xl p-5" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
              <div className="h-3 w-24 rounded" style={{ background: 'var(--muted)' }} />
              <div className="mt-3 h-8 w-32 rounded" style={{ background: 'var(--muted)' }} />
            </div>
          ))}
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-xl themed-card p-5 hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between">
                <div className="text-xs opacity-70">Total Users</div>
                <span className="text-theme-secondary">∞</span>
              </div>
              <div className="mt-2 text-3xl font-bold">{stats?.total}</div>
            </div>
            <div className="rounded-xl themed-card p-5 hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between">
                <div className="text-xs opacity-70">Real Users</div>
                <span className="text-theme-secondary">👤</span>
              </div>
              <div className="mt-2 text-3xl font-bold">{stats?.normal}</div>
            </div>
            <div className="rounded-xl themed-card p-5 hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between">
                <div className="text-xs opacity-70">Seeded Users</div>
                <span className="text-theme-secondary">🌱</span>
              </div>
              <div className="mt-2 text-3xl font-bold">{stats?.seeded}</div>
            </div>
          </div>

          {/* New Users Chart-like chips */}
          <section className="p-4 rounded-xl themed-card">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-heading">New users</h3>
              <div className="flex items-center gap-2">
                <button className="btn text-xs" onClick={() => setNewUsersOpen((v) => !v)}>{newUsersOpen ? 'Hide' : 'Show'}</button>
                <span className="text-xs opacity-70">{(stats?.newUsersDaily || []).length} days</span>
              </div>
            </div>
            {newUsersOpen && ((stats?.newUsersDaily || []).length === 0 ? (
              <div className="mt-3 text-xs text-theme-secondary">No recent signups.</div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mt-2 max-h-64 overflow-y-auto pr-1">
                {(stats?.newUsersDaily || []).map((r: any) => (
                  <div key={r._id} className="p-2 rounded-lg themed-card text-xs flex items-center justify-between">
                    <span>{r._id}</span><span className="font-semibold">{r.count}</span>
                  </div>
                ))}
              </div>
            ))}
          </section>

          {/* Recent Users List */}
          <section className="p-4 rounded-xl themed-card">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-heading">Recent users</h3>
              <button className="btn text-xs" onClick={() => setRecentOpen((v) => !v)}>{recentOpen ? 'Hide' : 'Show'}</button>
            </div>
            {recentOpen && ((stats?.recent || []).length === 0 ? (
              <div className="mt-3 text-xs text-theme-secondary">No recent users to display.</div>
            ) : (
              <ul className="text-xs mt-2 space-y-2 max-h-64 overflow-y-auto pr-1">
                {(stats?.recent || []).map((r: any) => (
                  <li key={r.id} className="rounded-lg p-2 themed-card hover:opacity-90 transition">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{r.id}</span>
                      {r.seeded && (
                        <span className="ml-1 px-2 py-0.5 rounded-full text-[10px] border" style={{ borderColor: 'var(--border)' }}>seeded</span>
                      )}
                    </div>
                    <div className="opacity-70">{new Date(r.joinedAt).toLocaleString()} • {r.role}</div>
                  </li>
                ))}
              </ul>
            ))}
          </section>
        </>
      )}
    </div>
  );
}
